import { useMemo, useCallback } from "react";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { DEFAULT_AXNUI_CONTENT } from "@iddataweb/idw-common-ui/constants";
import { LocaleTypes, ThemingGenericContentSlotTypes } from "@iddataweb/idw-common-ui/enums";
import {
    AXNUIContent,
    AXNUILayout,
    AXNUITheme,
    MultiLanguageWrapper
} from "@iddataweb/idw-common-ui/types";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { resources } from "@constants/hardcoded-strings";
import {
    AXNUI_CONTENT_PROPERTY_TYPE_ID,
    AXNUI_FORM_HELP_TEXT_TYPE_PROPERTY_TYPE_ID,
    AXNUI_LAYOUT_PROPERTY_TYPE_ID,
    AXNUI_THEME_PROPERTY_TYPE_ID,
    CANCEL_BUTTON_URL_PROPERTY_TYPE_ID,
    LOCALIZE_STRINGS_PROPERTY_TYPE_ID,
    MERGE_THEMING_AND_LOCALIZED_FILE_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { AXNUIThemingPreviewContainer } from "@containers/common/axnui-theming-preview-container/axnui-theming-preview-container";
import { ThemingSubTabContainer } from "@containers/common/theming-sub-tab-container/theming-sub-tab-container";
import { ThemingPolicyRetryMessageTabContainer } from "@containers/verification-service-details/verification-service-details-tab-container/verification-service-details-theming-tab/theming-policy-retry-message-tab-container/theming-policy-retry-message-tab-container";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { PageLevelTypes } from "@enums/page-level-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { SelectOption } from "@idw-types/common";
import { ThemingValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { FormattedRpServiceConfiguration, FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { getUpdatedPropertiesForContentPropertyUpdate } from "@utils/content-util";
import { sortNumbers } from "@utils/number-util";
import { getInheritedAndMergedStringPropertyValue } from "@utils/verification-service-details-util";

import { ThemingActionButtonTabContainer } from "./theming-action-button-tab-container/theming-action-button-tab-container";
import { ThemingCustomCssTabContainer } from "./theming-custom-css-tab-container/theming-custom-css-tab-container";
import { ThemingErrorPageTabContainer } from "./theming-error-page-tab-container/theming-error-page-tab-container";
import { ThemingFooterTabContainer } from "./theming-footer-tab-container/theming-footer-tab-container";
import { ThemingFormContentTabContainer } from "./theming-form-content-tab-container/theming-form-content-tab-container";
import { ThemingFormPanelTabContainer } from "./theming-form-panel-tab-container/theming-form-panel-tab-container";
import { ThemingHeaderTabContainer } from "./theming-header-tab-container/theming-header-tab-container";
import { ThemingKbaFormTabContainer } from "./theming-kba-form-tab-container/theming-kba-form-tab-container";
import { ThemingLandingPageTabContainer } from "./theming-landing-page-tab-container/theming-landing-page-tab-container";
import { ThemingLayoutTabContainer } from "./theming-layout-tab-container/theming-layout-tab-container";
import { ThemingMessagingTabContainer } from "./theming-messaging-tab-container/theming-messaging-tab-container";
import { ThemingMobileCompletePageTabContainer } from "./theming-mobile-complete-page-tab-container/theming-mobile-complete-page-tab-container";
import { ThemingPostFormContentSlotTabContainer } from "./theming-post-form-content-slot-tab-container/theming-post-form-content-slot-tab-container";
import { ThemingPostSubmitContentSlotTabContainer } from "./theming-post-submit-content-slot-tab-container/theming-post-submit-content-slot-tab-container";
import { ThemingPreFormContentSlotTabContainer } from "./theming-pre-form-content-slot-tab-container/theming-pre-form-content-slot-tab-container";
import { ThemingSuccessPageTabContainer } from "./theming-success-page-tab-container/theming-success-page-tab-container";
import { ThemingAsyncWaitingPageTabContainer } from "./theming-async-waiting-page-tab-container/theming-async-waiting-page-tab-container";
import { FormattedMessagingTemplate, MessagingTemplateConfiguration, MessagingTemplateCache } from "@idw-types/data/messaging-template";
import { AxnAttributeTypeField, AxnAttributeTypeFieldCustomization, AxnCompany, AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { EmailTemplateFields } from "@idw-types/common/email-template-fields";
import { MessagingTemplateTypes } from "@enums/messaging-template-types";
import { IDWSession } from "@idw-types/data/idw-session";
import { updateAllHelpTextCustomVariables } from "@utils/custom-html-and-css-util";
import { RedirectUrlInformation } from "@idw-types/redirect-url-information";
import { RedirectUrlUpdateModeTypes, updateRedirectUrls } from "@utils/redirect-urls/redirect-urls-util";
import { isNullOrUndefined } from "@utils/object-util";

type VerificationServiceDetailsThemingTabProps = {
    session: IDWSession;
    hasUnsubmittedChangeRequest: boolean;
    isDisabled: boolean;
    axnCompanies: Array<AxnCompany>;
    axnRelyingParties: Array<AxnRelyingParty>;
    selectedLanguage: LocaleTypes;
    availableLanguages: Array<LocaleTypes>;
    isMultiLanguageEnabled: boolean;
    multiLanguageAXNUIContent: MultiLanguageWrapper<AXNUIContent>;
    validationErrors: ThemingValidationErrors;
    hasDisplayableDocumentCaptureAttributes: boolean;
    showMessagingTab: boolean;
    showIncodeFlowTabs: boolean;
    availableMessagingTemplateTypes: Array<MessagingTemplateTypes>;
    messagingTemplateConfigurations: Array<MessagingTemplateConfiguration>;
    selectedMessagingTemplates: Array<FormattedMessagingTemplate>;
    showKbaFormTab: boolean;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUILayout: AXNUILayout;
    inheritedAXNUITheme: AXNUITheme;
    emailTemplateFields: EmailTemplateFields;
    inheritedEmailTemplateFields: EmailTemplateFields;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    isDisplayResultsEnabled: boolean;
    inheritedIsDisplayResultsEnabled: boolean;
    messagingTemplateCache: MessagingTemplateCache;
    emailFromAddress: string;
    redirectUrlsInformation: RedirectUrlInformation;
    customLanguages: Array<LocaleTypes>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    setMessagingTemplateCache: (messagingTemplateCache: MessagingTemplateCache) => void;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
    setHasFetchedMessagingTemplates: (messagingTemplateCache: MessagingTemplateCache) => void;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    updateProperties: (properties: Array<{ propertyTypeId: string, newValue: string, added: boolean }>) => void;
};

export const VerificationServiceDetailsThemingTab = (props: VerificationServiceDetailsThemingTabProps) => {
    const {
        session,
        hasUnsubmittedChangeRequest,
        isDisabled,
        axnCompanies,
        axnRelyingParties,
        selectedLanguage,
        availableLanguages,
        isMultiLanguageEnabled,
        multiLanguageAXNUIContent,
        validationErrors,
        rpServiceConfigurationState,
        hasDisplayableDocumentCaptureAttributes,
        showMessagingTab,
        messagingTemplateConfigurations,
        selectedMessagingTemplates,
        emailTemplateFields,
        showIncodeFlowTabs,
        showKbaFormTab,
        axnuiContent,
        axnuiLayout,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUILayout,
        inheritedAXNUITheme,
        isDisplayResultsEnabled,
        inheritedIsDisplayResultsEnabled,
        availableMessagingTemplateTypes,
        messagingTemplateCache,
        emailFromAddress,
        redirectUrlsInformation,
        customLanguages,
        axnAttributeTypeFields,
        axnAttributeTypeFieldCustomizations,
        setMessagingTemplateCache,
        setRpServiceConfigurationState,
        setHasFetchedMessagingTemplates,
        uploadFileToS3,
        updateProperties
    } = props;

    const permissionBasedIsDisabled = useMemo(() => {
        return (
            isDisabled ||
            !rpServiceConfigurationState.permissions.canModifyAXNUICustomization ||
            !rpServiceConfigurationState.permissions.canModifyBranding
        );
    }, [
        isDisabled,
        rpServiceConfigurationState.permissions.canModifyAXNUICustomization,
        rpServiceConfigurationState.permissions.canModifyBranding
    ]);
    const serviceProfileKey = `s${rpServiceConfigurationState.id}`;

    const updateAXNUIContentPropertyValue = (newAXNUIContent: AXNUIContent) => {
        const newProperties = getUpdatedPropertiesForContentPropertyUpdate(
            AXNUI_CONTENT_PROPERTY_TYPE_ID,
            rpServiceConfigurationState.properties,
            newAXNUIContent,
            multiLanguageAXNUIContent,
            availableLanguages,
            selectedLanguage,
            isMultiLanguageEnabled,
            DEFAULT_AXNUI_CONTENT
        );
        
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const updateAXNUILayoutPropertyValue = (axnuiLayout: AXNUILayout) => {
        const newValue = JSON.stringify(axnuiLayout);
        const newProperties = rpServiceConfigurationState.properties.map(property => {
            if (property.propertyTypeId === AXNUI_LAYOUT_PROPERTY_TYPE_ID) {
                return {
                    ...property,
                    value: newValue,
                    addedToService: true
                };
            } else {
                return property;
            }
        });
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const updateAXNUILayoutHtmlTextAndVariables = (axnuiLayout: AXNUILayout, axnuiContent: AXNUIContent, helpTextType: ThemingGenericContentSlotTypes) => {
        const newValue = JSON.stringify(axnuiLayout);
        const newProperties = rpServiceConfigurationState.properties.map(property => {
            if (property.propertyTypeId === AXNUI_LAYOUT_PROPERTY_TYPE_ID) {
                return {
                    ...property,
                    value: newValue,
                    addedToService: true
                };
            } else if (property.propertyTypeId === AXNUI_CONTENT_PROPERTY_TYPE_ID) {
                // update the help text custom variables content for all available languages with updated variables  
                const newValue = updateAllHelpTextCustomVariables(axnuiLayout.helpText[helpTextType].htmlText, 
                    helpTextType, 
                    isMultiLanguageEnabled, 
                    availableLanguages, 
                    multiLanguageAXNUIContent,
                    selectedLanguage,
                    axnuiContent,
                    DEFAULT_AXNUI_CONTENT
                );

                return {
                    ...property,
                    value: newValue,
                    addedToService: true
                };
            } else {
                return property;
            }
        });

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const updateAXNUIThemePropertyValue = (axnuiTheme: AXNUITheme) => {
        const newValue = JSON.stringify(axnuiTheme);
        const newProperties = rpServiceConfigurationState.properties.map(property => {
            if (property.propertyTypeId === AXNUI_THEME_PROPERTY_TYPE_ID) {
                return {
                    ...property,
                    value: newValue,
                    addedToService: true
                };
            } else {
                return property;
            }
        });
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const predefinedHelpTextTypeOptions: Array<SelectOption> = useMemo(() => {
        const property = rpServiceConfigurationState.properties.find(property => property.propertyTypeId === AXNUI_FORM_HELP_TEXT_TYPE_PROPERTY_TYPE_ID);
        if (property?.values) {
            try {
                const values: Array<SelectOption> = JSON.parse(property.values);
                values.push({ label: "Boku Consent Text", value: "bokuConsentText" });
                return values;
            } catch (error) {
                console.error(error);
            }
        }
        return [];
    }, [rpServiceConfigurationState.properties]);

    const activeAttributes = useMemo(() => {
        return rpServiceConfigurationState.attributes
            .filter(attribute => attribute.addedToService && attribute.status === EntityStatusTypes.ACTIVE)
            .sort((a, b) => sortNumbers(a.order, b.order));
    }, [rpServiceConfigurationState.attributes]);

    const localizeStrings = useMemo(() => {
        const {
            mergedValue
        } = getInheritedAndMergedStringPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            LOCALIZE_STRINGS_PROPERTY_TYPE_ID
        );

        return isStringTrue(mergedValue);
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const mergeThemingAndLocalizedFile = useMemo(() => {
        const {
            mergedValue
        } = getInheritedAndMergedStringPropertyValue(
            rpServiceConfigurationState.properties,
            rpServiceConfigurationState.appliedProfiles,
            MERGE_THEMING_AND_LOCALIZED_FILE_PROPERTY_TYPE_ID
        );

        return isStringTrue(mergedValue);
    }, [rpServiceConfigurationState.properties, rpServiceConfigurationState.appliedProfiles]);

    const hasCancelButtonURLEnabled: boolean = useMemo(()=> {
        const property = rpServiceConfigurationState.properties.find(property => property.propertyTypeId === CANCEL_BUTTON_URL_PROPERTY_TYPE_ID && property.addedToService);
        return !isNullOrUndefined(property?.value);
    }, [rpServiceConfigurationState.properties]);

    const generatePreviewComponent = useCallback((currentTab: AXNUIThemingTabTypes, previewableRedirectUrls?: Array<string>): React.ReactNode => {
        return (
            <AXNUIThemingPreviewContainer
                currentTab={currentTab}
                axnuiContent={axnuiContent}
                axnuiLayout={axnuiLayout}
                axnuiTheme={axnuiTheme}
                axnAttributeTypeFields={axnAttributeTypeFields}
                axnAttributeTypeFieldCustomizations={axnAttributeTypeFieldCustomizations}
                attributes={activeAttributes}
                localizeStrings={localizeStrings}
                mergeThemingAndLocalizedFile={mergeThemingAndLocalizedFile}
                activeLanguage={selectedLanguage}
                redirectUrls={previewableRedirectUrls}
                hasDisplayableDocumentCaptureAttributes={hasDisplayableDocumentCaptureAttributes}
                previewEntityType={PreviewEntityTypes.VERIFICATION_SERVICE}
                hasCancelButtonURLEnabled={hasCancelButtonURLEnabled}
            />
        );
    }, [
        axnuiContent,
        axnuiLayout,
        axnuiTheme,
        activeAttributes,
        localizeStrings,
        mergeThemingAndLocalizedFile,
        selectedLanguage,
        hasDisplayableDocumentCaptureAttributes
    ]);

    const handleRedirectChangeAtIndex = (newRedirectUrl: string, index: number) => {
        const newProperties = updateRedirectUrls(
            rpServiceConfigurationState.properties,
            serviceProfileKey,
            RedirectUrlUpdateModeTypes.EDIT,
            PageLevelTypes.APIKEY,
            newRedirectUrl,
            index
        );

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties as Array<FormattedRpServiceConfigurationProperty>
        });


    };

    const handleRemoveRedirect = (index: number) => {
        const newProperties = updateRedirectUrls(
            rpServiceConfigurationState.properties,
            serviceProfileKey,
            RedirectUrlUpdateModeTypes.REMOVE,
            PageLevelTypes.APIKEY,
            null,
            index
        );

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties as Array<FormattedRpServiceConfigurationProperty>
        });
    };

    const handleAddRedirect = (newRedirectUrl: string) => {
        const newProperties = updateRedirectUrls(
            rpServiceConfigurationState.properties,
            serviceProfileKey,
            RedirectUrlUpdateModeTypes.ADD,
            PageLevelTypes.APIKEY,
            newRedirectUrl
        );
        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties as Array<FormattedRpServiceConfigurationProperty>
        });
    };

    return (
        <ThemingSubTabContainer>
            <Tabs id="theming" unmountOnExit={true}>
                <Tab eventKey={AXNUIThemingTabTypes.LAYOUT} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.layout.tabName}>
                    <ThemingLayoutTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        axnuiLayout={axnuiLayout}
                        axnuiTheme={axnuiTheme}
                        inheritedAXNUILayout={inheritedAXNUILayout}
                        inheritedAXNUITheme={inheritedAXNUITheme}
                        pageLevel={PageLevelTypes.APIKEY}
                        customLanguages={customLanguages}
                        generatePreviewComponent={generatePreviewComponent}
                        uploadFileToS3={uploadFileToS3}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                        updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                    />
                </Tab>
                {axnuiLayout.isHeaderEnabled && (
                    <Tab eventKey={AXNUIThemingTabTypes.HEADER} title={validationErrors.header.tabTitle}>
                        <ThemingHeaderTabContainer
                            isDisabled={permissionBasedIsDisabled}
                            validationErrors={validationErrors.header}
                            axnuiContent={axnuiContent}
                            axnuiTheme={axnuiTheme}
                            inheritedAXNUIContent={inheritedAXNUIContent}
                            inheritedAXNUITheme={inheritedAXNUITheme}
                            pageLevel={PageLevelTypes.APIKEY}
                            generatePreviewComponent={generatePreviewComponent}
                            uploadFileToS3={uploadFileToS3}
                            updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                            updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                        />
                    </Tab>
                )}
                {axnuiLayout.isFooterEnabled && (
                    <Tab eventKey={AXNUIThemingTabTypes.FOOTER} title={validationErrors.footer.tabTitle}>
                        <ThemingFooterTabContainer
                            isDisabled={permissionBasedIsDisabled}
                            validationErrors={validationErrors.footer}
                            axnuiContent={axnuiContent}
                            axnuiTheme={axnuiTheme}
                            inheritedAXNUIContent={inheritedAXNUIContent}
                            inheritedAXNUITheme={inheritedAXNUITheme}
                            pageLevel={PageLevelTypes.APIKEY}
                            generatePreviewComponent={generatePreviewComponent}
                            updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                            updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                        />
                    </Tab>
                )}
                <Tab eventKey={AXNUIThemingTabTypes.ACTION_BUTTON} title={validationErrors.actionButton.tabTitle}>
                    <ThemingActionButtonTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.actionButton}
                        axnuiContent={axnuiContent}
                        axnuiTheme={axnuiTheme}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        inheritedAXNUITheme={inheritedAXNUITheme}
                        pageLevel={PageLevelTypes.APIKEY}
                        hasDisplayableDocumentCaptureAttributes={hasDisplayableDocumentCaptureAttributes}
                        generatePreviewComponent={generatePreviewComponent}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                    />
                </Tab>
                <Tab eventKey={AXNUIThemingTabTypes.FORM_PANEL} title={validationErrors.formPanel.tabTitle}>
                    <ThemingFormPanelTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.formPanel}
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        axnuiTheme={axnuiTheme}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        inheritedAXNUILayout={inheritedAXNUILayout}
                        inheritedAXNUITheme={inheritedAXNUITheme}
                        pageLevel={PageLevelTypes.APIKEY}
                        generatePreviewComponent={generatePreviewComponent}
                        uploadFileToS3={uploadFileToS3}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                        updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                    />
                </Tab>
                <Tab eventKey={AXNUIThemingTabTypes.PRE_FORM_CONTENT_SLOT} title={validationErrors.preFormContentSlot.tabTitle}>
                    <ThemingPreFormContentSlotTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.preFormContentSlot}
                        axnuiLayout={axnuiLayout}
                        axnuiContent={axnuiContent}
                        predefinedHelpTextTypeOptions={predefinedHelpTextTypeOptions}
                        generatePreviewComponent={generatePreviewComponent}
                        uploadFileToS3={uploadFileToS3}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUILayoutHtmlTextAndVariables={updateAXNUILayoutHtmlTextAndVariables}
                    />
                </Tab>
                {hasDisplayableDocumentCaptureAttributes && (
                    <Tab eventKey={AXNUIThemingTabTypes.FORM_CONTENT} title={validationErrors.formContent.tabTitle}>
                        <ThemingFormContentTabContainer
                            isDisabled={permissionBasedIsDisabled}
                            validationErrors={validationErrors.formContent}
                            inheritedAXNUIContent={inheritedAXNUIContent}
                            axnuiContent={axnuiContent}
                            pageLevel={PageLevelTypes.APIKEY}
                            generatePreviewComponent={generatePreviewComponent}
                            updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        />
                    </Tab>
                )}
                <Tab eventKey={AXNUIThemingTabTypes.POST_FORM_CONTENT_SLOT} title={validationErrors.postFormContentSlot.tabTitle}>
                    <ThemingPostFormContentSlotTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.postFormContentSlot}
                        axnuiLayout={axnuiLayout}
                        predefinedHelpTextTypeOptions={predefinedHelpTextTypeOptions}
                        selectedLanguage={selectedLanguage}
                        axnuiContent={axnuiContent}
                        isMultiLanguageEnabled={isMultiLanguageEnabled}
                        generatePreviewComponent={generatePreviewComponent}
                        uploadFileToS3={uploadFileToS3}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                        updateAXNUILayoutHtmlTextAndVariables={updateAXNUILayoutHtmlTextAndVariables}
                    />
                </Tab>
                <Tab eventKey={AXNUIThemingTabTypes.POST_SUBMIT_CONTENT_SLOT} title={validationErrors.postSubmitContentSlot.tabTitle}>
                    <ThemingPostSubmitContentSlotTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.postSubmitContentSlot}
                        axnuiLayout={axnuiLayout}
                        axnuiContent={axnuiContent}
                        predefinedHelpTextTypeOptions={predefinedHelpTextTypeOptions}
                        generatePreviewComponent={generatePreviewComponent}
                        uploadFileToS3={uploadFileToS3}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUILayoutHtmlTextAndVariables={updateAXNUILayoutHtmlTextAndVariables}
                    />
                </Tab>
                <Tab eventKey={AXNUIThemingTabTypes.SUCCESS_PAGE} title={validationErrors.successPage.tabTitle}>
                    <ThemingSuccessPageTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.successPage}
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        inheritedAXNUILayout={inheritedAXNUILayout}
                        pageLevel={PageLevelTypes.APIKEY}
                        generatePreviewComponent={generatePreviewComponent}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                    />
                </Tab>
                <Tab eventKey={AXNUIThemingTabTypes.ERROR_PAGE} title={validationErrors.errorPage.tabTitle}>
                    <ThemingErrorPageTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.errorPage}
                        axnuiContent={axnuiContent}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        pageLevel={PageLevelTypes.APIKEY}
                        generatePreviewComponent={generatePreviewComponent}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                    />
                </Tab>
                {isStringTrue(rpServiceConfigurationState.usePolicyManager) && (
                    <Tab eventKey={AXNUIThemingTabTypes.POLICY_RETRY_MESSAGE} title={validationErrors.policyRetryMessage.tabTitle}>
                    <ThemingPolicyRetryMessageTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        axnuiTheme={axnuiTheme}
                        inheritedAXNUITheme={inheritedAXNUITheme}
                        axnuiContent={axnuiContent}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        pageLevel={PageLevelTypes.APIKEY}
                        validationErrors={validationErrors.policyRetryMessage}
                        rpServiceConfigurationState={rpServiceConfigurationState}
                        generatePreviewComponent={generatePreviewComponent}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                    />
                </Tab>)}
                <Tab eventKey={AXNUIThemingTabTypes.LANDING_PAGE} title={validationErrors.landingPage.tabTitle}>
                    <ThemingLandingPageTabContainer
                        isDisabled={isDisabled}
                        isServiceBelongToWorkflow={rpServiceConfigurationState.workflowID !== null}
                        validationErrors={validationErrors.landingPage}
                        axnuiContent={axnuiContent}
                        axnuiLayout={axnuiLayout}
                        isDisplayResultsEnabled={isDisplayResultsEnabled}
                        inheritedIsDisplayResultsEnabled={inheritedIsDisplayResultsEnabled}
                        redirectUrlsInformation={redirectUrlsInformation}
                        inheritedAXNUIContent={inheritedAXNUIContent}
                        inheritedAXNUILayout={inheritedAXNUILayout}
                        pageLevel={PageLevelTypes.APIKEY}
                        generatePreviewComponent={generatePreviewComponent}
                        handleAddRedirect={handleAddRedirect}
                        handleRemoveRedirect={handleRemoveRedirect}
                        handleRedirectChangeAtIndex={handleRedirectChangeAtIndex}
                        updateProperties={updateProperties}
                        updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        updateAXNUILayoutPropertyValue={updateAXNUILayoutPropertyValue}
                    />
                </Tab>
                {showKbaFormTab && (
                    <Tab eventKey={AXNUIThemingTabTypes.KBA_FORM} title={validationErrors.kbaForm.tabTitle}>
                        <ThemingKbaFormTabContainer
                            isDisabled={permissionBasedIsDisabled}
                            validationErrors={validationErrors.kbaForm}
                            axnuiContent={axnuiContent}
                            inheritedAXNUIContent={inheritedAXNUIContent}
                            pageLevel={PageLevelTypes.APIKEY}
                            generatePreviewComponent={generatePreviewComponent}
                            updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        />
                    </Tab>
                )}
                {showIncodeFlowTabs && (
                    <Tab eventKey={AXNUIThemingTabTypes.ASYNC_WAITING_PAGE} title={validationErrors.asyncWaitingPage.tabTitle}>
                        <ThemingAsyncWaitingPageTabContainer
                            isDisabled={permissionBasedIsDisabled}
                            validationErrors={validationErrors.asyncWaitingPage}
                            axnuiContent={axnuiContent}
                            inheritedAXNUIContent={inheritedAXNUIContent}
                            pageLevel={PageLevelTypes.APIKEY}
                            generatePreviewComponent={generatePreviewComponent}
                            updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        />
                    </Tab>
                )}
                {showIncodeFlowTabs && (
                    <Tab eventKey={AXNUIThemingTabTypes.MOBILE_COMPLETE_PAGE} title={validationErrors.mobileCompletePage.tabTitle}>
                        <ThemingMobileCompletePageTabContainer
                            isDisabled={permissionBasedIsDisabled}
                            validationErrors={validationErrors.mobileCompletePage}
                            axnuiContent={axnuiContent}
                            inheritedAXNUIContent={inheritedAXNUIContent}
                            pageLevel={PageLevelTypes.APIKEY}
                            generatePreviewComponent={generatePreviewComponent}
                            updateAXNUIContentPropertyValue={updateAXNUIContentPropertyValue}
                        />
                    </Tab>
                )}
                <Tab eventKey={AXNUIThemingTabTypes.CUSTOM_CSS} title={validationErrors.customCss.tabTitle}>
                    <ThemingCustomCssTabContainer
                        isDisabled={permissionBasedIsDisabled}
                        validationErrors={validationErrors.customCss}
                        axnuiTheme={axnuiTheme}
                        generatePreviewComponent={generatePreviewComponent}
                        updateAXNUIThemePropertyValue={updateAXNUIThemePropertyValue}
                    />
                </Tab>
                {showMessagingTab && (
                    <Tab eventKey={AXNUIThemingTabTypes.MESSAGING} title={validationErrors.messaging.tabTitle}>
                        <ThemingMessagingTabContainer
                            session={session}
                            hasUnsubmittedChangeRequest={hasUnsubmittedChangeRequest}
                            isDisabled={permissionBasedIsDisabled}
                            axnCompanies={axnCompanies}
                            axnRelyingParties={axnRelyingParties}
                            validationErrors={validationErrors.messaging}
                            selectedLanguage={selectedLanguage}
                            rpServiceConfigurationState={rpServiceConfigurationState}
                            messagingTemplateConfigurations={messagingTemplateConfigurations}
                            availableMessagingTemplateTypes={availableMessagingTemplateTypes}
                            selectedMessagingTemplates={selectedMessagingTemplates}
                            messagingTemplateCache={messagingTemplateCache}
                            emailTemplateFields={emailTemplateFields}
                            emailFromAddress={emailFromAddress}
                            setMessagingTemplateCache={setMessagingTemplateCache}
                            setRpServiceConfigurationState={setRpServiceConfigurationState}
                            setHasFetchedMessagingTemplates={setHasFetchedMessagingTemplates}
                        />
                    </Tab>
                )}
            </Tabs>
        </ThemingSubTabContainer>
    );
};